<template>
  <label
    :class="[rootClass, b(modifiers)]"
    @mouseenter="hasHover = true"
    @mouseleave="hasHover = false"
  >
    <input
      v-bind="$attrs"
      v-model="internalValue"
      :class="b('field', fieldModifiers)"
      :disabled="disabled"
      :value="value"
      :name="name"
      type="radio"
    />
    <span :class="b('radio')"></span>
    <span :class="b('label-text')">
      <slot></slot>
    </span>
  </label>
</template>

<script>
  import designMixin from '@/mixins/design.js';
  import formStates from '@/mixins/form-states.js';

  /**
   * Renders a radio element. Use a v-for loop to generate a set of radio buttons.
   *
   * The displayed name can either be provided by the property `displayName` or as a slot.
   */
  export default {
    name: 'e-radio',

    // components: {},
    mixins: [formStates, designMixin],
    inheritAttrs: false,

    props: {
      /**
       * Used by v-model.
       */
      modelValue: {
        default: '',
        type: [String, Number, Boolean],
      },

      /**
       * Adds value attribute.
       */
      value: {
        type: [String, Number, Boolean],
        default: null,
      },

      /**
       * Adds name attribute.
       */
      name: {
        required: true,
        type: String,
      },

      /**
       * Set the placement of the button. Only available in v2 design.
       */
      buttonPosition: {
        type: String,
        default: 'left',
        validator(value) {
          return ['left', 'right'].includes(value);
        },
      },
    },

    emits: ['update:modelValue'],

    // data() {
    //   return {};
    // },

    computed: {
      /**
       * Getter/setter for the internal value.
       *
       * @returns {Boolean}
       */
      internalValue: {
        get() {
          return this.modelValue;
        },
        set(value) {
          this.$emit('update:modelValue', value);
        },
      },

      /**
       * Returns a configuration Object for modifier classes.
       *
       * @returns {Object}
       */
      modifiers() {
        const { buttonPosition, stateModifiers, designModifiers, internalValue } = this;

        return {
          ...stateModifiers,
          ...designModifiers,
          selected: internalValue === this.value,
          buttonPosition,
        };
      },

      /**
       * Returns all modifiers for the field class.
       *
       * @returns {Object}
       */
      fieldModifiers() {
        return {
          selected: this.internalValue === this.value,
        };
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    // methods: {},
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss-v2/component' as v2;

  .e-radio {
    $this: &;
    $label-size: 16px;

    position: relative;
    display: block;
    cursor: pointer;
    font-size: functions.get-theme-var('e-radio-font-size');
    line-height: functions.get-theme-var('e-radio-line-height');

    @include mixins.media(lg) {
      font-size: functions.get-theme-var('e-radio-font-size--lg');
    }

    &--disabled {
      cursor: default;
    }

    &__radio {
      &::before,
      &::after {
        position: absolute;
        top: 0.25em;
        left: 0;
        content: '';
        width: functions.rem($label-size);
        height: functions.rem($label-size);
        border: 1px solid functions.get-theme-var('e-radio-button-border-color');
        border-radius: 50%;
        background: functions.get-theme-var('e-radio-background-color');
        transition: opacity 200ms ease-in-out;
      }

      &::after {
        top: calc(0.25em + 1px);
        left: 1px;
        width: calc(#{functions.rem($label-size)} - 2px);
        height: calc(#{functions.rem($label-size)} - 2px);
        opacity: 0;
        border: functions.rem(3px) solid functions.get-theme-var('e-radio-background-color');
        background-color: functions.get-theme-var('e-radio-button-border-color');
      }
    }

    &__label-text {
      display: block; // Keep indent.
      margin: 0;
      padding-left: functions.rem(variables.$spacing--25);
      color: functions.get-theme-var('e-radio-label-color');
    }

    &__field {
      position: absolute;
      left: -200vw;
      -webkit-appearance: none;
      appearance: none;
    }

    &__field:checked {
      + #{$this}__radio::after {
        opacity: 1;
      }

      ~ #{$this}__label-text {
        color: functions.get-theme-var('e-radio-label-color--checked');
      }
    }

    &__field:disabled {
      + #{$this}__radio {
        cursor: default;

        &::before {
          border-color: functions.get-theme-var('e-radio-button-border-color--disabled');
        }

        &::after {
          background-color: functions.get-theme-var('e-radio-button-border-color--disabled');
        }
      }

      ~ #{$this}__label-text {
        cursor: default;
        color: functions.get-theme-var('e-radio-label-color--disabled');
      }
    }
  }

  ///
  /// Design V2
  ///
  @include v2.define-theme-properties-for(
    'default',
    'e-radio',
    (
      'button-color': var(--color-primary--1),
      'button-color--disabled': var(--color-gray--300),
      'button-background': transparent,
      'button-background--hover': var(--color-gray--50),
      'button-background--error': var(--color-red--100),
      'button-background--warning': var(--color-yellow--100),
      'button-background--success': var(--color-green--100),
      'button-background--info': var(--color-blue--100),
      'button-outer-size--large': 24px,
      'button-outer-size--medium': 20px,
      'button-outer-size--small': 16px,
      'button-inner-size--large': 12px,
      'button-inner-size--medium': 10px,
      'button-inner-size--small': 8px,
      'button-padding--large': 4px,
      'button-padding--medium': 4px,
      'button-padding--small': 4px,
      'label-margin--large': 10px,
      'label-margin--medium': 8px,
      'label-margin--small': 6px,
      'label-color': inherit,
      'label-color--checked': var(--color-primary--1),
      'label-color--disabled': var(--color-gray--300),
    )
  );

  .e-radio {
    $this: &;

    &--design-design2 {
      --transition: var(--duration-regular) ease-in-out;
      --button-outer-width: 2px;

      display: flex;
      gap: var(--label-margin);
      align-items: flex-start;

      &:not(#{$this}--disabled) {
        &:hover {
          #{$this}__radio {
            background-color: var(--e-radio-button-background--hover);
          }
        }
      }

      #{$this}__radio {
        --button-inner-offset: calc(
          (var(--button-outer-size) - var(--button-inner-size)) * 0.5 + var(--button-padding)
        );

        @include v2.transition-all(background-color, var(--transition));

        position: relative;
        padding: var(--button-padding);
        border-radius: 50%;
        background-color: var(--e-radio-button-background);

        &::before,
        &::after {
          @include v2.transition-all(opacity border background, var(--transition));

          display: block;
          box-sizing: content-box;
          border-color: var(--e-radio-button-color);
          background: var(--e-radio-button-color);
        }

        &::before {
          position: relative;
          top: 0;
          left: 0;
          width: calc(var(--button-outer-size) - var(--button-outer-width) * 2);
          height: calc(var(--button-outer-size) - var(--button-outer-width) * 2);
          border-width: var(--button-outer-width);
          background: none;
        }

        &::after {
          top: var(--button-inner-offset);
          left: var(--button-inner-offset);
          width: var(--button-inner-size);
          height: var(--button-inner-size);
          border: 0;
        }
      }

      #{$this}__label-text {
        @include v2.transition-all(color, var(--transition));

        padding-top: calc(var(--button-padding) + var(--button-outer-width));
        padding-left: 0;
        color: var(--e-radio-label-color);
      }

      #{$this}__field:checked {
        ~ #{$this}__label-text {
          color: var(--e-radio-label-color--checked);
        }
      }

      #{$this}__field:disabled {
        + #{$this}__radio {
          &::before {
            border-color: var(--e-radio-button-color--disabled);
          }

          &::after {
            background-color: var(--e-radio-button-color--disabled);
          }
        }

        ~ #{$this}__label-text {
          color: var(--e-radio-label-color--disabled);
        }
      }

      &#{$this}--state-error {
        #{$this}__radio {
          background-color: var(--e-radio-button-background--error);
        }
      }

      &#{$this}--state-warning {
        #{$this}__radio {
          background-color: var(--e-radio-button-background--warning);
        }
      }

      &#{$this}--state-success {
        #{$this}__radio {
          background-color: var(--e-radio-button-background--success);
        }
      }

      &#{$this}--state-info {
        #{$this}__radio {
          background-color: var(--e-radio-button-background--info);
        }
      }
    }

    &--design-size-large {
      --label-margin: var(--e-radio-label-margin--large);
      --button-padding: var(--e-radio-button-padding--large);
      --button-outer-size: var(--e-radio-button-outer-size--large);
      --button-inner-size: var(--e-radio-button-inner-size--large);

      #{$this}__label-text {
        @include v2.font-body--large();
      }
    }

    &--design-size-medium {
      --label-margin: var(--e-radio-label-margin--medium);
      --button-padding: var(--e-radio-button-padding--medium);
      --button-outer-size: var(--e-radio-button-outer-size--medium);
      --button-inner-size: var(--e-radio-button-inner-size--medium);

      #{$this}__label-text {
        @include v2.font-body--medium();
      }
    }

    &--design-size-small {
      --label-margin: var(--e-radio-label-margin--small);
      --button-padding: var(--e-radio-button-padding--small);
      --button-outer-size: var(--e-radio-button-outer-size--small);
      --button-inner-size: var(--e-radio-button-inner-size--small);

      #{$this}__label-text {
        @include v2.font-body--small();
      }
    }

    &--button-position-right {
      flex-direction: row-reverse;
    }
  }
</style>
